import "@/App.css";
import React, { useEffect } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";
import Home from "@/pages/Home";
import BmwPage from "@/pages/BmwPage";
import KlimaPage from "@/pages/KlimaPage";
import AutomatavaltoPage from "@/pages/AutomatavaltoPage";
import DiohejasTisztitasPage from "@/pages/DiohejasTisztitasPage";
import KisteherauPage from "@/pages/KisteherauPage";
import FordEcoboostPage from "@/pages/FordEcoboostPage";
import Impresszum from "@/pages/Impresszum";
import AdatkezelesiTajekoztato from "@/pages/AdatkezelesiTajekoztato";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import FloatingCta from "@/components/FloatingCta";

// Scrolls to the top on every route change EXCEPT when there is a #hash
// anchor (in which case the page should jump to that section instead).
function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const id = hash.replace("#", "");
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }
    }
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [pathname, hash]);
  return null;
}

// Legacy URL → home + scroll to section.
// (Plain <Navigate to="/#hash" /> breaks React Router v7 Routes — bug #13179)
function HashRedirect({ hash }) {
  const navigate = useNavigate();
  useEffect(() => {
    navigate("/", { replace: true });
    if (hash) {
      const id = hash.replace("#", "");
      setTimeout(() => {
        document
          .getElementById(id)
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 150);
    }
  }, [navigate, hash]);
  return null;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/bmw" element={<BmwPage />} />
          {/* SEO aliases for BMW + MINI keyword targeting */}
          <Route path="/bmw-mini-szerviz" element={<Navigate to="/bmw" replace />} />
          <Route path="/bmw-szerviz" element={<Navigate to="/bmw" replace />} />
          <Route path="/mini-szerviz" element={<Navigate to="/bmw" replace />} />
          <Route path="/mini-szerviz-debrecen" element={<Navigate to="/bmw" replace />} />
          <Route path="/mini-javitas" element={<Navigate to="/bmw" replace />} />
          <Route path="/mini" element={<Navigate to="/bmw" replace />} />
          <Route path="/klima" element={<KlimaPage />} />
          {/* Common klíma URL aliases (SEO + legacy) */}
          <Route path="/klima-toltes" element={<Navigate to="/klima" replace />} />
          <Route path="/klima-javitas" element={<Navigate to="/klima" replace />} />
          <Route path="/auto-klima" element={<Navigate to="/klima" replace />} />
          <Route path="/klimatoltes" element={<Navigate to="/klima" replace />} />

          <Route
            path="/automatavalto-olajcsere"
            element={<AutomatavaltoPage />}
          />
          {/* Common automataváltó URL aliases (SEO + legacy) */}
          <Route
            path="/automata-valto-olajcsere"
            element={<Navigate to="/automatavalto-olajcsere" replace />}
          />
          <Route
            path="/valto-olajcsere"
            element={<Navigate to="/automatavalto-olajcsere" replace />}
          />
          <Route
            path="/motulevo"
            element={<Navigate to="/automatavalto-olajcsere" replace />}
          />
          <Route
            path="/zf-olajcsere"
            element={<Navigate to="/automatavalto-olajcsere" replace />}
          />

          <Route
            path="/diohejas-tisztitas"
            element={<DiohejasTisztitasPage />}
          />
          {/* SEO aliases for walnut blasting + redirect from old misspelled URL */}
          <Route
            path="/diohejjas-tisztitas"
            element={<Navigate to="/diohejas-tisztitas" replace />}
          />
          <Route
            path="/diohej-tisztitas"
            element={<Navigate to="/diohejas-tisztitas" replace />}
          />
          <Route
            path="/walnut-blasting"
            element={<Navigate to="/diohejas-tisztitas" replace />}
          />
          <Route
            path="/szelep-tisztitas"
            element={<Navigate to="/diohejas-tisztitas" replace />}
          />
          <Route
            path="/koksz-eltavolitas"
            element={<Navigate to="/diohejas-tisztitas" replace />}
          />

          <Route
            path="/kisteher-szerviz"
            element={<KisteherauPage />}
          />
          {/* SEO aliases for kisteher / Ducato */}
          <Route
            path="/kisteherauto-szerviz"
            element={<Navigate to="/kisteher-szerviz" replace />}
          />
          <Route
            path="/fiat-ducato-szerviz"
            element={<Navigate to="/kisteher-szerviz" replace />}
          />
          <Route
            path="/ducato-szerviz"
            element={<Navigate to="/kisteher-szerviz" replace />}
          />
          <Route
            path="/flotta-szerviz"
            element={<Navigate to="/kisteher-szerviz" replace />}
          />
          <Route path="/ford-ecoboost-felujitas" element={<FordEcoboostPage />} />
          <Route path="/ford-ecoboost" element={<Navigate to="/ford-ecoboost-felujitas" replace />} />
          <Route path="/ford-motorfelujitas" element={<Navigate to="/ford-ecoboost-felujitas" replace />} />
          <Route path="/ecoboost-felujitas" element={<Navigate to="/ford-ecoboost-felujitas" replace />} />
          <Route path="/blokkperselyezes" element={<Navigate to="/ford-ecoboost-felujitas" replace />} />
          <Route path="/impresszum" element={<Impresszum />} />
          <Route
            path="/adatkezelesi-tajekoztato"
            element={<AdatkezelesiTajekoztato />}
          />

          {/* Legacy WordPress URL redirects (still in Google index) */}
          <Route
            path="/szolgaltatasok"
            element={<HashRedirect hash="szolgaltatasok" />}
          />
          <Route
            path="/szolgaltatasok/"
            element={<HashRedirect hash="szolgaltatasok" />}
          />
          <Route
            path="/automata-valto-karbantartas"
            element={<Navigate to="/automatavalto-olajcsere" replace />}
          />
          <Route
            path="/automata-valto-karbantartas/"
            element={<Navigate to="/automatavalto-olajcsere" replace />}
          />
          <Route
            path="/q-service-partner"
            element={<HashRedirect hash="szolgaltatasok" />}
          />
          <Route
            path="/q-service-partner/"
            element={<HashRedirect hash="szolgaltatasok" />}
          />
          <Route
            path="/kapcsolat"
            element={<HashRedirect hash="kapcsolat" />}
          />
          <Route
            path="/kapcsolat/"
            element={<HashRedirect hash="kapcsolat" />}
          />
          <Route
            path="/galeria"
            element={<HashRedirect hash="galeria" />}
          />
          <Route
            path="/galeria/"
            element={<HashRedirect hash="galeria" />}
          />
          <Route path="/blog" element={<Navigate to="/" replace />} />
          <Route path="/blog/" element={<Navigate to="/" replace />} />

          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<AdminDashboard />} />

          {/* Catch-all → home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <FloatingCta />
      </BrowserRouter>
    </div>
  );
}

export default App;
